import React from 'react';
import {ButtonDropdown, DropdownList, EMPHASIS, FlexRow} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportExport} from './useReportExport';

/** The Download control, shared by both top bars so they cannot drift apart again — the bars having
 * carried two different Download controls is what made the same button look like two different
 * things depending on the mode, and what left a Cypress spec depending on a hook that existed in only
 * one of them.
 *
 * The design asks for a SINGLE monochrome button — leading download glyph, "Download" label, inline
 * trailing chevron — not a split button with a detached caret. `ButtonDropdown` supplies both glyphs,
 * and being a real button it shares its height, label weight, padding and radius with the Share and
 * Discard buttons beside it. The menu carries the export formats; CSV is the only one the report
 * service produces today, and keeping it a menu means a second format is one more entry rather than a
 * new control.
 *
 * Owns its own export state rather than taking it as props: both bars used it for nothing else, and
 * the live region that announces progress has to travel with the control that starts it. */
export function ReportDownloadMenu(): JSX.Element {
	const {formatMessage} = useIntl();
	const download = useReportExport();

	return (
		<>
			{/* Pinned to `flex: 0 0 auto`: DS `FlexRow` defaults to `flex: 1`, so an unpinned wrapper
			 * stretches this control across the whole bar instead of letting it hug its label. */}
			<FlexRow alignItems={'center'} flex={'0 0 auto'} data-cy={'ai-report-download-pin'}>
				<DropdownList>
					<DropdownList.Trigger>
						{/* While an export runs the trigger is closed off so a second one cannot be started,
						 * and the label says why — the busy wording carries it rather than a spinner, so the
						 * control keeps its width. `aria-busy` states the same thing to assistive tech, which
						 * cannot see the label change. */}
						<ButtonDropdown
							emphasis={EMPHASIS.MONO}
							icon={'download'}
							disabled={download.exporting}
							aria-busy={download.exporting}
							aria-label={download.label}
							data-cy={'ai-report-download-button'}
						>
							{download.label}
						</ButtonDropdown>
					</DropdownList.Trigger>
					<DropdownList.Content offset={4}>
						<DropdownList.Item onClick={download.start} data-cy={'ai-report-download-csv'}>
							{formatMessage({id: 'ai_report_builder.download_csv'})}
						</DropdownList.Item>
					</DropdownList.Content>
				</DropdownList>
			</FlexRow>
			{download.statusRegion}
		</>
	);
}
